import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface MarketplaceStore {
  id: string;
  name: string;
  image_url: string;
}

const StoresNearYou = () => {
  const [stores, setStores] = useState<MarketplaceStore[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('id, name, image_url')
          .eq('business_type', 'marketplace')
          .not('image_url', 'is', null)
          .neq('image_url', '')
          .neq('image_url', '/placeholder.svg');

        if (error) throw error;

        const filtered = (data || []).filter(s => s.image_url && s.image_url.trim() !== '');
        setStores(filtered);
      } catch (error) {
        console.error('Error fetching marketplace stores:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  if (!loading && stores.length === 0) return null;

  // Arrange stores into rows of 4 for the 2-row horizontal scroll grid
  const rows: MarketplaceStore[][] = [];
  for (let i = 0; i < stores.length; i += 2) {
    rows.push(stores.slice(i, Math.min(i + 2, stores.length)));
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-3">Stores near you</h2>
      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex flex-col items-center gap-2 flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-muted animate-pulse" />
              <div className="w-16 h-3 bg-muted animate-pulse rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex gap-5 w-max">
            {rows.map((pair, colIdx) => (
              <div key={colIdx} className="flex flex-col gap-4">
                {pair.map(store => (
                  <button
                    key={store.id}
                    onClick={() => navigate(`/marketplace/vendor/${store.id}`)}
                    className="flex flex-col items-center gap-1.5 w-20"
                  >
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border bg-card shadow-sm">
                      <img
                        src={store.image_url}
                        alt={store.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground text-center truncate w-full">
                      {store.name}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoresNearYou;
